import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal, { SweetAlertResult } from "sweetalert2";
import CustomOtpInput from "../../../components/common/CustomOtpInput";
import {
  getItem,
  setItem,
  setItemWithObject,
} from "../../../utils/cache/LocalStorage";
import { POST } from "../../../services/HTTPServices";
import {
  alertMessage,
  injectCustomStyles2,
} from "../../../services/alertService";
import { ALERT_TITLES, ALERT_MESSAGES } from "../../../constants";
import {
  Title,
  Description,
  EmailSpan,
  OtpInputContainer,
  ResendContainer,
  ResendText,
  ResendLink,
} from "../../../styles/OTPVerificationStep";
import {
  BackButton,
  NextButton,
  FormContainer,
  ButtonContainer,
  ErrorText,
  FullWidthContainer,
  LoadingSpinner,
} from "../../../styles/PersonalInfoStep";
import { OnBoardingStepProps } from "../../../constants";
import { LoadingSpinnerIcon, LoadingOverlay } from "../../../styles/loading";
import Back from "../../../assets/svgs/LoginSignup/Step1/back.svg";

const OTPVerificationStep: React.FC<OnBoardingStepProps> = ({
  onBack,
  onNext,
  onChange,
  formData,
  onAssign,
}) => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [showCountdown, setShowCountdown] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const tempEmail = getItem("provider_login_email") || "";
    if (tempEmail) {
      setEmail(tempEmail);
    }
  }, []);

  useEffect(() => {
    if (showCountdown) {
      const timer = setTimeout(() => {
        if (countdown > 0) {
          setCountdown((prev) => prev - 1);
        } else {
          setShowCountdown(false);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown, showCountdown]);

  const handleChangeOTP = (otpValue: string) => {
    setOtp(otpValue);
  };

  const handleResend = async () => {
    setLoading(true);
    const url = "serviceprovider/ResendOTP";
    const payload = { email };
    const response = (await POST(url, payload, "provider")) as
      | { status: number; message: string }
      | undefined;

    setLoading(false);
    if (!response) {
      const errorMessage = "An unknown error occurred";
      alertMessage(ALERT_TITLES.ERROR, errorMessage);
      setErrorMessage(errorMessage);
      return;
    }

    if (response.status === 200) {
      alertMessage(ALERT_TITLES.SUCCESS, ALERT_MESSAGES.OTP_RESENT);
      setShowCountdown(true);
      setCountdown(30);
    } else if (response.status === 401) {
      navigate("/unauthorized-page");
    } else {
      alertMessage(ALERT_TITLES.ERROR, response.message);
      setErrorMessage(response.message);
    }
  };

  const confirm = async () => {
    if (!otp || otp.length < 6) {
      setErrorMessage("Please enter the 6-digit verification code.");
      return;
    }
    setErrorMessage("");
    setLoading(true);
    const url = "serviceprovider/SPConfirmation";
    const payload = { email: email, otpCode: otp };

    const response = (await POST(url, payload, "provider")) as
      | { status: number; message: string; data: { token: string } }
      | undefined;

    setLoading(false);
    if (!response) {
      const errorMessage = "An unknown error occurred";
      alertMessage(ALERT_TITLES.ERROR, errorMessage);
      setErrorMessage(errorMessage);
      return;
    }

    if (response.status === 200) {
      setItemWithObject("provider-otp", { email, otp });
      setItem("temp_token", response.data.token);
      onNext();
    } else if (response.status === 401) {
      navigate("/unauthorized-page");
    } else {
      alertMessage(ALERT_TITLES.ERROR, response.message);
      setErrorMessage(response.message);
    }
  };

  const back = () => {
    alertMessageWithCancel(
      ALERT_TITLES.WARNING,
      ALERT_MESSAGES.UNSAVED_CHANGES
    );
  };

  const alertMessageWithCancel = (title: string, text: string) => {
    injectCustomStyles2();

    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: "btn btn-success",
        cancelButton: "btn btn-danger",
      },
      buttonsStyling: false,
    });

    swalWithBootstrapButtons
      .fire({
        title: title,
        text: text,
        icon:
          title === "Error!"
            ? "error"
            : title === "Success!"
            ? "success"
            : "warning",
        confirmButtonText: "OK",
        showCancelButton: true,
         customClass: {
      confirmButton: title === "Error!"? "custom-error-btn" : "custom-confirm-btn",
      cancelButton: "custom-cancel-btn",
    },
      })
      .then((result: SweetAlertResult) => {

        if (result.isConfirmed) {
          const provider_login_email =
            localStorage.getItem("provider_login_email") || "";
          if (provider_login_email) {
            const provider_login_mobile =
              localStorage.getItem("provider_login_mobile") || "";
            if (provider_login_mobile) {
              rollback(provider_login_email, provider_login_mobile);
            }
          }
        }
      });
  };

  const rollback = async (email: string, mobile: string) => {
    setLoading(true);

    let url = "serviceprovider/RollbackMerchant";

    let temp = {
      email: email,
      mobile: "+65" + mobile,
    };

    let res = (await POST(url, temp, "provider")) as
      | { status: number; message: string }
      | undefined;

    setLoading(false);
    if (!res) {
      alertMessage(ALERT_TITLES.ERROR, "An unknown error occurred");
      return;
    }

    if (res.status === 200) {
      Object.keys(localStorage).filter(k => k !== 'accountId' && k !== 'login_credentials').forEach(k => localStorage.removeItem(k));      
    } else if (res.status === 401) {
      navigate("/unauthorized-page");
    } else {
      alertMessage(ALERT_TITLES.ERROR, res.message);
    }
  };

  return (
    <>
      {loading && (
        <LoadingOverlay>
          <LoadingSpinner>
            <LoadingSpinnerIcon />
          </LoadingSpinner>
        </LoadingOverlay>
      )}
      <FormContainer>
        <Title>Enter OTP</Title>
        <Description>
          An OTP is sent to your email <EmailSpan>{email}</EmailSpan>.
        </Description>
        <OtpInputContainer>
          <CustomOtpInput
            value={otp}
            onChange={handleChangeOTP}
            numInputs={6}
            separator={<span></span>}
            inputClassName="otp-input"
            isInputSecure={true}
            shouldAutoFocus={true}
            isInputNum={true}
          />
        </OtpInputContainer>
        <ResendContainer>
          <ResendText>Didn't receive the OTP?</ResendText>
          {showCountdown ? (
            <ResendLink $isActive={false}>Resend in {countdown}s</ResendLink>
          ) : (
            <ResendLink $isActive={true} onClick={handleResend}>
              Click here to send again
            </ResendLink>
          )}
        </ResendContainer>
        {errorMessage && (
          <ErrorText marginBottom="32px">{errorMessage}</ErrorText>
        )}
        <FullWidthContainer>
          <ButtonContainer>
            <BackButton onClick={back}>
              <img src={Back} alt="Back" />
            </BackButton>
            <NextButton onClick={confirm}>Confirm to Signup</NextButton>
          </ButtonContainer>
        </FullWidthContainer>
      </FormContainer>
    </>
  );
};

export default OTPVerificationStep;
